import { Stack, Text, Card, Group, Badge, Alert, Loader } from "@mantine/core";
import {
	IconDeviceMobile,
	IconShieldCheck,
	IconClock,
	IconInfoCircle,
	IconAlertTriangle,
} from "@tabler/icons-react";
import { useUserMFAStatus } from "../hooks/useUserMFAStatus";
import { DisableTOTPButton } from "./DisableTOTPButton";
import { FreshAuthRequired } from "./FreshAuthRequired";
import { SetupAuthenticatorAppButton } from "./SetupAuthenticatorAppButton";

interface TOTPStatusProps {
	onRefresh?: () => void;
	setupAuthenticatorAppModalSize?: "md" | "lg" | "xl";
}

export function TOTPStatus({
	onRefresh,
	setupAuthenticatorAppModalSize = "lg",
}: TOTPStatusProps) {
	const mfaStatus = useUserMFAStatus();

	if (mfaStatus.isLoading) {
		return (
			<Card withBorder p="md">
				<Group>
					<Loader size="sm" />
					<Text size="sm">Loading authenticator status...</Text>
				</Group>
			</Card>
		);
	}

	if (mfaStatus.needsFreshAuth) {
		return <FreshAuthRequired />;
	}

	if (mfaStatus.error) {
		return (
			<Alert color="red" icon={<IconInfoCircle size={16} />}>
				<Text size="sm">Failed to load MFA status: {mfaStatus.error}</Text>
			</Alert>
		);
	}

	if (!mfaStatus.hasTOTP) {
		return (
			<Card withBorder p="md">
				<Stack gap="sm">
					<Group>
						<IconDeviceMobile size={20} color="gray" />
						<Text size="sm" c="dimmed">
							No authenticator apps configured
						</Text>
					</Group>
					<Text size="xs" c="dimmed">
						Set up an authenticator app for additional security
					</Text>
					<SetupAuthenticatorAppButton
						modalSize={setupAuthenticatorAppModalSize}
						onSuccess={onRefresh}
					/>
				</Stack>
			</Card>
		);
	}

	return (
		<Stack gap="sm">
			<Card withBorder p="md">
				<Stack gap="sm">
					<Group justify="space-between" align="flex-start">
						<Group>
							<IconShieldCheck size={20} color="green" />
							<Stack gap={0}>
								<Text size="sm" fw={500}>
									Authenticator App
								</Text>
								<Text size="xs" c="dimmed">
									Time-based one-time password (TOTP)
								</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Badge
								color={mfaStatus.preferredMFA === "TOTP" ? "green" : "gray"}
								variant="light"
								size="sm"
							>
								{mfaStatus.preferredMFA === "TOTP" ? "Preferred" : "Available"}
							</Badge>
						</Group>
					</Group>

					<Group gap="xs" mt="xs">
						<IconClock size={14} color="gray" />
						<Text size="xs" c="dimmed">
							Generates new codes every 30 seconds
						</Text>
					</Group>

					{mfaStatus.canSwitchToSMS && (
						<Text size="xs" c="green">
							✓ SMS MFA is available as fallback
						</Text>
					)}

					<Group justify="flex-end" mt="sm">
						<DisableTOTPButton onSuccess={onRefresh} />
					</Group>
				</Stack>
			</Card>
		</Stack>
	);
}
